/**
 * useBulkSelfHeal: run "Self-Heal Now" over a selection of print-file issues.
 *
 * A browser loop over the existing per-issue mutation
 * (`printAgentAdmin.selfHealCustomNow`), bounded by `runBulkSelfHeal` (3 in
 * flight, one retry of a busy answer). No new server endpoint and no extra DB
 * reads per row beyond what the single mutation already does. Each call
 * answers within ~20 s server-side, so no request nears the proxy timeout.
 *
 * Stop finishes the heals already running and starts no more. Leaving the
 * page (unmount) presses Stop, so a run never carries on out of sight with no
 * way to stop it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { runBulkSelfHeal, type BulkSelfHealTally } from '../bulkSelfHeal';
import type { SelfHealNowResult } from './useSelfHealCustomNow';

export interface BulkSelfHealProgress {
  done: number;
  total: number;
}

export function useBulkSelfHeal() {
  const utils = trpc.useUtils();
  const [progress, setProgress] = useState<BulkSelfHealProgress | null>(null);
  const [stopping, setStopping] = useState(false);
  const stopRef = useRef(false);

  // Unmount mid-run: start no more heals (the ones already running finish).
  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );

  const run = useCallback(
    async (ids: string[]): Promise<BulkSelfHealTally> => {
      stopRef.current = false;
      setStopping(false);
      setProgress({ done: 0, total: ids.length });
      try {
        return await runBulkSelfHeal(
          ids,
          async (issueId) =>
            (await utils.client.printAgentAdmin.selfHealCustomNow.mutate({
              issueId,
            })) as SelfHealNowResult,
          {
            shouldStop: () => stopRef.current,
            onSettled: () =>
              setProgress((p) => (p ? { ...p, done: p.done + 1 } : p)),
          },
        );
      } finally {
        setProgress(null);
        setStopping(false);
        // Healed rows leave the list, requeued/failed rows change their pill:
        // refresh the list, the badges and the "Select all N" ids together.
        void utils.productionOrders.issues.list.invalidate();
        void utils.productionOrders.issues.getCounts.invalidate();
        void utils.productionOrders.issues.listSelfHealIds.invalidate();
      }
    },
    [utils],
  );

  const stop = useCallback(() => {
    stopRef.current = true;
    setStopping(true);
  }, []);

  return { run, stop, progress, running: progress !== null, stopping };
}
